// src/contexts/FabricServiceContext.tsx

import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { ItemType } from '@theodoreniu/realtime-api-beta/dist/lib/client';
import { FabricClient } from '../fabric/client';
import { RealtimeEvent } from '../utils/events';
import { useAuth } from './AuthContext';
import { fabric_instructions } from '../utils/fabric_conversation_config';
import { Voice, VOICE_OPTIONS } from '../types/Voice';

interface FabricInstance {
    id: string;
    seed_id: string;
    parent_id?: string;
    name: string;
    created_at: Date;
    model_name: string;
    entity_name?: string;
}

interface FabricFile {
    id: string;
    name: string;
    user: string;
    agent: string;
    description?: string;
    is_default?: boolean;
    index: number;
}

interface FabricProgress {
    percentComplete: number;
    timeRemaining: string;
    processedItems: number;
    totalItems: number;
    startTime: number;
}

interface ModelOption {
    provider: string;
    model: string;
}

export interface MemoryIndexes {
    id: string;
    indexes: number[];
}

interface FabricServiceContextType {
    isFabric: boolean;
    fabricInstructions: string;
    fabricEvents: RealtimeEvent[];
    fabricBuilderEvents: RealtimeEvent[];
    hasFabricServer: boolean;
    isFabricConnected: boolean;
    isFabricConnecting: boolean;
    isGeneratingInstance: boolean;
    generationProgress: FabricProgress;
    items: any[];
    fabricItems: ItemType[];
    memoryIndexes: MemoryIndexes[];
    fabricVoice: Voice;
    modelOptions: ModelOption[];
    model: string;
    fabricTemperature: number;
    useSystemTwo: boolean;
    pendingQueueCount: number;
    instanceOptions: FabricInstance[];
    instance?: FabricInstance;
    fileOptions: FabricFile[];
    file?: FabricFile;
    isFileUploading: boolean;
    eventFilters: (string | undefined)[];
    conversationFilters: (string | undefined)[];
    setEventFilters: React.Dispatch<React.SetStateAction<(string | undefined)[]>>;
    setConversationFilters: React.Dispatch<React.SetStateAction<(string | undefined)[]>>;
    setIsFabric: React.Dispatch<React.SetStateAction<boolean>>;
    setFabricEvents: React.Dispatch<React.SetStateAction<RealtimeEvent[]>>;
    setItems: React.Dispatch<React.SetStateAction<any[]>>;
    setFabricItems: React.Dispatch<React.SetStateAction<ItemType[]>>;
    setFabricVoice: React.Dispatch<React.SetStateAction<Voice>>;
    setModel: React.Dispatch<React.SetStateAction<string>>;
    setFabricTemperature: React.Dispatch<React.SetStateAction<number>>;
    setUseSystemTwo: React.Dispatch<React.SetStateAction<boolean>>;
    setInstance: React.Dispatch<React.SetStateAction<FabricInstance | undefined>>
    setFile: React.Dispatch<React.SetStateAction<FabricFile | undefined>>
    uploadFile: (file: File) => Promise<FabricFile | undefined>;
    replaceFile: (fileId: string, file: File) => Promise<void>;
    downloadFile: (fileId: string, fileName: string) => Promise<void>;
    deleteFile: (fileId: string) => Promise<void>;
    connectFabric: () => Promise<void>;
    disconnectFabric: () => Promise<void>;
    resetFabric: () => Promise<void>;
    deleteConversationItem: (itemId: string) => Promise<void>;
    sendMessage: (message: string) => Promise<void>;
    resumeSession: () => Promise<void>;
    triggerPresetMessage: () => Promise<void>;
    createFabricResponse: (inputMessage: string, completion: (outputMessage: string) => void) => Promise<void>;
    generateInstance: () => Promise<any>;
    deleteInstance: (id?: string) => Promise<any>;
}

const FabricServiceContext = createContext<FabricServiceContextType | undefined>(undefined);

const DEFAULT_PROGRESS: FabricProgress = {
    percentComplete: 0,
    timeRemaining: '00:00',
    processedItems: 0,
    totalItems: 0,
    startTime: 0
};

export const FabricServiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isFabric, setIsFabric] = useState(true);  //Temp enable Fabric by default
    const [fabricInstructions, setFabricInstructions] = useState(fabric_instructions);
    const [items, setItems] = useState<any[]>([]);
    const [fabricItems, setFabricItems] = useState<ItemType[]>([]);
    const [fabricEvents, setFabricEvents] = useState<RealtimeEvent[]>([]);
    const [fabricBuilderEvents, setFabricBuilderEvents] = useState<RealtimeEvent[]>([]);
    const [hasFabricServer, setHasFabricServer] = useState(true)
    const [isFabricConnected, setIsFabricConnected] = useState(false);
    const [isFabricConnecting, setIsFabricConnecting] = useState(false);
    const [isGeneratingInstance, setIsGeneratingInstance] = useState(false);
    const [generationProgress, setGenerationProgress] = useState<FabricProgress>({ ...DEFAULT_PROGRESS });
    const [fabricVoice, setFabricVoice] = useState(VOICE_OPTIONS[0]);
    const [modelOptions, setModelOptions] = useState<ModelOption[]>([])
    const [model, setModel] = useState('')
    const [fabricTemperature, setFabricTemperature] = useState(0)
    const [instanceOptions, setInstanceOptions] = useState<FabricInstance[]>([]);
    const [fileOptions, setFileOptions] = useState<FabricFile[]>([]);
    const [file, setFile] = useState<FabricFile>();
    const [instance, setInstance] = useState<FabricInstance>()
    const [useSystemTwo, setUseSystemTwo] = useState(true);
    const [memoryIndexes, setMemoryIndexes] = useState<MemoryIndexes[]>([]);
    const [pendingQueueCount, setPendingQueueCount] = useState(0);
    const [eventFilters, setEventFilters] = useState<(string | undefined)[]>([]);
    const [conversationFilters, setConversationFilters] = useState<(string | undefined)[]>([]);

    const { getFabricClientConfig, updateApiKey, apiKey } = useAuth();
    const clientRef = useRef<FabricClient>(new FabricClient(getFabricClientConfig()));
    const clientBuilderRef = useRef<FabricClient>(new FabricClient(getFabricClientConfig()));
    const progressTrackingRef = useRef({ ...DEFAULT_PROGRESS });
    const [isFileUploading, setIsFileUploading] = useState(false);

    // Format Time Remaining for Progress
    const formatTimeRemaining = (seconds: number): string => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const connectFabric = useCallback(async () => {
        const client = clientRef.current;
        setIsFabricConnecting(true);

        try {
            setFabricEvents([]);
            setFabricItems([]);
            setItems(client.getItems());
            await client.connect('fabric', () => {
                const result = clientRef.current.updateSession();
                return result
            });
        } catch (error) {
            console.error('Connection failed:', error);
            setIsFabricConnecting(false);
            setIsFabricConnected(false);
        }
    }, []);

    const disconnectFabric = useCallback(async () => {
        setIsFabricConnected(false);
        setFabricEvents([]);
        setFabricItems([]);
        setItems([]);
        setPendingQueueCount(0);
        setMemoryIndexes([])

        const client = clientRef.current;
        client.disconnect();
    }, []);

    const resetFabric = useCallback(async () => {
        await disconnectFabric();
        await connectFabric();
    }, [connectFabric, disconnectFabric]);

    // If connected send a ping event every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            if (isFabricConnected) {
                const client = clientRef.current;

                try {
                    client.send('fabric.session.connection.ping');
                }
                catch (error) {
                    resetFabric()
                }
            }
        }, 10000);

        return () => clearInterval(interval);
    }, [isFabricConnected]);

    const deleteConversationItem = useCallback(async (id: string) => {
        const client = clientRef.current;
        client.deleteItem(id);
    }, []);

    const sendMessage = useCallback(async (message: string) => {
        const client = clientRef.current;
        client.send('fabric.conversation.item.create', {
            item: {
                type: 'message',
                role: 'user',
                content: [
                    {
                        type: 'text',
                        text: message,
                    },
                ],
            },
        });
    }, []);

    const resumeSession = useCallback(async () => {
        const client = clientRef.current;
        client.send('fabric.conversation.item.create', {
            item: {
                type: 'request',
                content: [
                    {
                        type: 'text',
                        text: '<RESUME-CHAT>',
                    },
                ],
            },
        });
    }, []);

    const triggerPresetMessage = useCallback(async () => {
        const client = clientRef.current;
        client.send('fabric.conversation.item.create', {
            item: {
                type: 'request',
                content: [
                    {
                        type: 'text',
                        text: '<AUTO-REPLY>',
                    },
                ],
            },
        });
    }, []);

    const createFabricResponse = useCallback(async (inputMessage: string, completion: (outputMessage: string) => void) => {
        const client = clientRef.current;
        if (!client) throw new Error('FabricClient is not initialized');

        client.send("fabric.conversation.item.create", {
            item: {
                "type": "message",
                "role": "user",
                "content": [
                    {
                        "type": "input_text",
                        "text": inputMessage
                    }
                ]
            },
        });

        client.send("fabric.response.create")

        //[TODO]: need to handle waiting for async response

        // const message = {
        //     "instructions": "You are being provided an approved response. Attempt to closely follow this guidance.",
        //     "response": "This would be the perfect message from Fabric."
        // };

        // completion(JSON.stringify(message));
    }, []);

    // useEffect(() => {

    //     console.log(items)

    // }, [fabricEvents, items])

    const ingestInstanceOptions = (instances: FabricInstance[]) => {
        instances.forEach((instance: FabricInstance) => {
            instance.created_at = new Date(instance.created_at);
        });
        setInstanceOptions(instances)
        return instances
    }

    const generateInstance = useCallback(async () => {
        const client = clientBuilderRef.current;
        if (!file || !model) return;

        const resetProgress = { ...DEFAULT_PROGRESS };
        setGenerationProgress(resetProgress);
        progressTrackingRef.current = { ...resetProgress };

        setFabricBuilderEvents([]);
        await client.connect("instances/connection");
    }, [file, model, fabricTemperature]);

    const deleteInstance = useCallback(async (instanceId?: string) => {
        const client = clientRef.current;
        await client.deleteInstance(instanceId)
            .then((response) => {
                if (response?.["status"] === 'success') {
                    client.getInstances().then((instances) => {
                        ingestInstanceOptions(instances)
                    });
                    if (!instanceId || instanceId === instance?.id) {
                        localStorage.removeItem('tmp::fabric_scenario_instance');
                        setInstance(undefined);
                    }
                }
            });
    }, []);

    const uploadFile = async (file: File): Promise<FabricFile | undefined> => {
        try {
            setIsFileUploading(true);
            const client = clientRef.current;
            const response = await client.uploadFile(file);

            // Refresh file list
            const files = await client.getFiles();
            setFileOptions(files);
            return response;
        } catch (error) {
            console.error('Error uploading file:', error);
            throw error;
        } finally {
            setIsFileUploading(false);
        }
    };

    const replaceFile = async (fileId: string, file: File) => {
        try {
            setIsFileUploading(true);
            const client = clientRef.current;
            await client.replaceFile(fileId, file);
            // Refresh file list
            const files = await client.getFiles();
            setFileOptions(files);
        } catch (error) {
            console.error('Error replacing file:', error);
            throw error;
        } finally {
            setIsFileUploading(false);
        }
    };

    const downloadFile = async (fileId: string, fileName: string) => {
        try {
            const client = clientRef.current;
            await client.downloadFile(fileId, fileName);
        } catch (error) {
            console.error('Error downloading file:', error);
            throw error;
        }
    };

    const deleteFile = async (fileId: string) => {
        try {
            const client = clientRef.current;
            await client.deleteFile(fileId);
            // Refresh file list
            const files = await client.getFiles();
            setFileOptions(files);
            localStorage.removeItem('tmp::fabric_scenario_file')
            setFile(undefined)
            client.getInstances().then((instances) => {
                ingestInstanceOptions(instances)
            });
        } catch (error) {
            console.error('Error deleting file:', error);
            throw error;
        }
    };

    useEffect(() => {
        const client = clientRef.current;
        client.getActivation().then((activation) => {
            updateApiKey(activation)
        })
            .catch((error) => {
                setHasFabricServer(false)
            });
    }, []);

    useEffect(() => {
        if (!apiKey) return
        const client = clientRef.current;
        client.getModels().then((models) => {
            const options = Object.entries(models).flatMap(
                ([provider, modelList]) =>
                    (modelList as string[]).map((model) => ({
                        provider,
                        model,
                    }))
            );
            setModelOptions(options)
            setHasFabricServer(true)
        })
            .catch((error) => {
                setHasFabricServer(false)
            });
        client.getInstances().then((instances) => {
            ingestInstanceOptions(instances)
            setHasFabricServer(true)
        })
            .catch((error) => {
                setHasFabricServer(false)
            });
        client.getFiles().then((files) => {
            setFileOptions(files)
            setHasFabricServer(true)
        })
            .catch((error) => {
                setHasFabricServer(false)
            });
    }, [apiKey]);

    useEffect(() => {
        const client = clientBuilderRef.current;
        client.on('fabric.event', async (fabricEvent: RealtimeEvent) => {
            if (fabricEvent.event.type === 'fabric.instance.connected') {
                // Reset progress tracking when generation starts
                const newProgress = {
                    ...DEFAULT_PROGRESS,
                    startTime: Date.now()
                };
                progressTrackingRef.current = newProgress;
                setGenerationProgress(newProgress);

                setIsGeneratingInstance(true);
                client.send('fabric.instance.generate', {
                    instance: {
                        file_id: file?.id,
                        model: model,
                        temperature: fabricTemperature,
                    },
                });
            }
            else if (fabricEvent.event.type === 'fabric.instance.generate.output_item.added') {
                const status = fabricEvent.event.item.status as { index: number, count: number };

                if (status) {
                    const progress = progressTrackingRef.current;

                    progress.processedItems = status.index;
                    progress.totalItems = status.count;
                    progress.percentComplete = Math.floor((status.index / status.count) * 100);

                    const elapsedMs = Date.now() - progress.startTime;
                    const itemsPerSecond = progress.processedItems / (elapsedMs / 1000);
                    const remainingItems = progress.totalItems - progress.processedItems;

                    if (itemsPerSecond > 0) {
                        const secondsRemaining = remainingItems / itemsPerSecond;
                        progress.timeRemaining = formatTimeRemaining(secondsRemaining);
                    }

                    setGenerationProgress(progress);
                }
            }
            else if (fabricEvent.event.type === 'fabric.instance.generate.done') {
                const instance_id = fabricEvent.event?.["instance"]?.["id"];
                client.getInstances().then((instances) => {
                    const updatedInstances = ingestInstanceOptions(instances);
                    const newInstance = updatedInstances.find((instance: FabricInstance) => instance.id === instance_id);
                    if (!isFabricConnected && newInstance) {
                        localStorage.setItem('tmp::fabric_scenario_instance', newInstance.id || '');
                        setInstance(newInstance);
                    }
                });
                setIsGeneratingInstance(false);
                client.disconnect();
            }

            setFabricBuilderEvents(prev => [...prev, fabricEvent]);
        });

        client.on('error', (event: any) => console.error(event));

        return () => {
            client.reset();
        };
    }, [clientBuilderRef.current, file, model, fabricTemperature, isFabricConnected]);

    useEffect(() => {
        if (!isGeneratingInstance) return;

        const intervalId = setInterval(() => {
            const progress = progressTrackingRef.current;
            setGenerationProgress(progress);
        }, 500); // Update twice per second

        return () => clearInterval(intervalId);
    }, [isGeneratingInstance]);

    useEffect(() => {
        const client = clientRef.current;

        client.updateSession({
            model: model,
            temperature: fabricTemperature,
            instance_id: instance?.id,
            seed_id: instance?.seed_id,
            should_modify: false,
            use_system_two: useSystemTwo
        });

        // Handle fabric events from client + server for event logging
        client.on('fabric.event', async (fabricEvent: RealtimeEvent) => {
            if (fabricEvent.event.type === 'fabric.session.created') {
                setIsFabricConnecting(false);
                setIsFabricConnected(true);
            }
            // Handle monitoring the pending queue
            if (fabricEvent.event.type === 'fabric.conversation.item.queue.updated') {
                setPendingQueueCount(fabricEvent.event.output_item.pending_count)
            }

            // Handle conversation item creation
            if (fabricEvent.event.type === 'fabric.conversation.item.created' &&
                fabricEvent.event.item.role === 'user') {
                // client.cancelResponse("");
            }

            //Update Memory Event with Selections
            if (fabricEvent.event.type === 'fabric.response.output_item.added' &&
                fabricEvent.event.item.output[0].fabric_type === 'memory_indexes') {
                const indexes_match: number[] = fabricEvent.event.item.output[0].content
                setMemoryIndexes((indexes) => indexes.concat({ id: fabricEvent.event.response_id, indexes: indexes_match }));
            }

            setFabricEvents((events) => events.concat(fabricEvent));
        });

        client.on('error', (event: any) => console.error(event));

        client.on('conversation.updated', async ({ item }: any) => {
            const items = client.getItems();
            setItems(items);
        });

        setItems(client.getItems());

        return () => {
            client.reset();
        };
    }, [clientRef.current, model, fabricTemperature, instance, useSystemTwo]);

    return (
        <FabricServiceContext.Provider
            value={{
                isFabric,
                fabricInstructions,
                fabricEvents,
                fabricBuilderEvents,
                hasFabricServer,
                isFabricConnected,
                isFabricConnecting,
                isGeneratingInstance,
                generationProgress,
                items,
                fabricItems,
                memoryIndexes,
                fabricVoice,
                modelOptions,
                model,
                fabricTemperature,
                useSystemTwo,
                pendingQueueCount,
                instanceOptions,
                instance,
                fileOptions,
                file,
                isFileUploading,
                eventFilters,
                conversationFilters,
                setEventFilters,
                setConversationFilters,
                setIsFabric,
                setFabricEvents,
                setItems,
                setFabricItems,
                setFabricVoice,
                setModel,
                setFabricTemperature,
                setUseSystemTwo,
                setInstance,
                setFile,
                uploadFile,
                replaceFile,
                downloadFile,
                deleteFile,
                connectFabric,
                disconnectFabric,
                resetFabric,
                deleteConversationItem,
                sendMessage,
                resumeSession,
                triggerPresetMessage,
                createFabricResponse,
                generateInstance,
                deleteInstance,
            }}
        >
            {children}
        </FabricServiceContext.Provider>
    );
};

export const useFabricService = () => {
    const context = useContext(FabricServiceContext);
    if (context === undefined) {
        throw new Error('useFabricService must be used within a FabricServiceProvider');
    }
    return context;
};